import { useEffect, useState, useRef } from "react";
import { Button, ConfigProvider, message, Input } from "antd";
import "./Login.scss";
import formlogo from "../../assets/svgs/formlogo.svg";
import { EmailIcon } from "../../assets/svgs/EmailIcon";
import { LockIcon } from "../../assets/svgs/LockIcon";
import { EyeIcon } from "../../assets/svgs/EyeIcon";
import { GoogleIcon } from "../../assets/svgs/GoogleIcon";
import { beLogin, googleLogin, firebaseLogin, resetLoginState } from "../../store/login.slice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { getSession } from "../../utils/session";
import LoginService, { ACCESS_DENIED_ERROR_MESSAGE } from "../../services/login";

const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Login = () => {
  const [values, setValues] = useState({
    email: "",
    pass: "",
  });
  const [emailError, setEmailError] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const beLoginState = useSelector((state: any) => state.login.beLogin);
  const googleLoginState = useSelector((state: any) => state.login.googleLogin);
  const firebaseLoginState = useSelector(
    (state: any) => state.login.firebaseLogin
  );
  
  // Track if we've already dispatched firebaseLogin for the current login attempt
  // This prevents infinite loops when firebaseLogin fails
  const firebaseLoginDispatchedRef = useRef(false);
  // Track the login attempt to reset the dispatch flag on new attempts
  const loginAttemptIdRef = useRef(0);

  useEffect(() => {
    setLoading(
      beLoginState.loading ||
        googleLoginState.loading ||
        firebaseLoginState.loading
    );
  }, [
    beLoginState.loading,
    googleLoginState.loading,
    firebaseLoginState.loading,
  ]);

  // Handle Google login errors (like popup cancelled)
  useEffect(() => {
    if (googleLoginState.error && !googleLoginState.loading) {
      // Error occurred and loading is false, ensure buttons are re-enabled
      setLoading(false);
      // Reset the dispatch flag so user can try again
      firebaseLoginDispatchedRef.current = false;
    }
  }, [googleLoginState.error, googleLoginState.loading]);

  // Handle email login errors
  useEffect(() => {
    if (beLoginState.error && !beLoginState.loading) {
      // Error occurred and loading is false, ensure buttons are re-enabled
      setLoading(false);
      // Reset the dispatch flag so user can try again
      firebaseLoginDispatchedRef.current = false;
    }
  }, [beLoginState.error, beLoginState.loading]);

  // Handle Firebase login errors
  useEffect(() => {
    if (firebaseLoginState.error && !firebaseLoginState.loading) {
      // Firebase login failed, ensure buttons are re-enabled
      setLoading(false);
      // DO NOT reset firebaseLoginDispatchedRef here - this prevents infinite loop
      // The ref will be reset when user initiates a NEW login attempt
      // The saga reduces thrown errors to a plain { message, status, code }
      // object (no `name`), so we match on the message to detect this case.
      const isAccessDenied =
        firebaseLoginState.error?.message === ACCESS_DENIED_ERROR_MESSAGE;
      message.error(
        isAccessDenied
          ? ACCESS_DENIED_ERROR_MESSAGE
          : "Authentication failed. Please try logging in again."
      );
    }
  }, [firebaseLoginState.error, firebaseLoginState.loading]);

  const handleEmailBlur = () => {
    setEmailError(!!values.email && !validateEmail(values.email));
  };

  const handleSubmit = async () => {
    if (values.email === "" || values.pass === "") {
      message.error("Please fill your email id and password to continue.");
      return;
    }

    // Email validation
    if (!validateEmail(values.email)) {
      setEmailError(true);
      message.error("Please enter a valid email address.");
      return;
    }

    // Reset state for new login attempt
    loginAttemptIdRef.current += 1;
    firebaseLoginDispatchedRef.current = false;
    dispatch(resetLoginState());

    try {
      dispatch(
        beLogin({
          email: values.email,
          password: values.pass,
        })
      );
    } catch (error) {
      console.error("Error during email login:", error);
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    // Reset state for new login attempt
    loginAttemptIdRef.current += 1;
    firebaseLoginDispatchedRef.current = false;
    dispatch(resetLoginState());

    try {
      dispatch(googleLogin());
    } catch (error) {
      console.error("Error during Google login:", error);
      setLoading(false);
    }
  };

  // Trigger firebaseLogin after backend login succeeds
  // CRITICAL: This effect must NOT re-dispatch if firebaseLogin already failed
  useEffect(() => {
    const customToken = getSession("customToken");
    
    // Guard 1: Both login methods must be done loading
    if (beLoginState.loading || googleLoginState.loading) {
      return;
    }
    
    // Guard 2: Must have a custom token
    if (!customToken) {
      return;
    }
    
    // Guard 3: firebaseLogin must not already be in progress (service-level mutex)
    if (LoginService.isFirebaseLoginInProgress()) {
      return;
    }
    
    // Guard 4: We must not have already dispatched for this login attempt
    if (firebaseLoginDispatchedRef.current) {
      return;
    }
    
    // Guard 5: firebaseLogin must not already have succeeded
    if (firebaseLoginState.data?.uid) {
      return;
    }
    
    // Guard 6: firebaseLogin must not be currently loading
    if (firebaseLoginState.loading) {
      return;
    }
    
    // Guard 7: firebaseLogin must not have failed (prevents infinite loop!)
    if (firebaseLoginState.error) {
      return;
    }
    
    // All guards passed - dispatch firebaseLogin
    firebaseLoginDispatchedRef.current = true;
    dispatch(firebaseLogin());
  }, [
    beLoginState.loading,
    beLoginState.data,
    googleLoginState.loading,
    googleLoginState.data,
    firebaseLoginState.loading,
    firebaseLoginState.data?.uid,
    firebaseLoginState.error,
    dispatch,
  ]);

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div className="login">
      <section id="containerWrapper">
        <div className="formcontainer">
          <div className="heading">
            <img className="egIcon1" src={formlogo} loading="lazy" alt="" />
            <div className="introcontent">
              <h3 className="welcome-to-shopeg">Welcome to ShopEG!</h3>
              <div className="login-to-your">Login to your account</div>
            </div>
          </div>
          <div className="formcontent">
            <div className="forminputcontent">
              <div className="inputfields">
                <div className="inputWrapper">
                  <Input
                    className="input"
                    name="emailAddress"
                    size="middle"
                    placeholder="Email Address"
                    type="email"
                    bordered={true}
                    prefix={<EmailIcon />}
                    status={emailError ? "error" : ""}
                    onChange={(e) => {
                      setValues((prev) => ({ ...prev, email: e.target.value }));
                      if (emailError) setEmailError(false);
                    }}
                    onBlur={handleEmailBlur}
                    onKeyPress={handleKeyPress}
                  />
                  {emailError && (
                    <div className="emailError">
                      Please enter a valid email address
                    </div>
                  )}
                </div>
                <Input.Password
                  className="passwordInputpassword"
                  name="passwordField"
                  size="middle"
                  placeholder="Enter Password"
                  bordered={true}
                  prefix={<LockIcon />}
                  suffix={<EyeIcon />}
                  onChange={(e) =>
                    setValues((prev) => ({ ...prev, pass: e.target.value }))
                  }
                  onKeyPress={handleKeyPress}
                />
              </div>
              {/* <Button
                className="recoverPassword"
                name="forgotPasswordButton"
                type="text"
              >
                Recover Password
              </Button> */}
            </div>
            {/* <button className="dontHaveAnContainer">
              <span className="dontHaveAn">{`Don’t have an account? `}</span>
              <span className="signUp">Sign Up</span>
            </button> */}
            <div className="loginButtons">
              <ConfigProvider
                theme={{
                  token: {
                    colorPrimary: "#18b400",
                  },
                  components: {
                    Button: {
                      colorBgContainerDisabled: "rgba(24, 180, 0, 0.8)",
                    },
                  },
                }}
              >
                <Button
                  className="buttons"
                  name="loginButton"
                  size="large"
                  shape="default"
                  type="primary"
                  onClick={handleSubmit}
                  loading={loading && beLoginState.loading}
                  disabled={loading || emailError || !values.email || !values.pass}
                >
                  {!(loading && beLoginState.loading) && "Login"}
                </Button>
              </ConfigProvider>
              <p className="or">OR</p>
              <Button
                className="googleLoginButton"
                name="googleLoginButton"
                size="large"
                shape="default"
                onClick={handleGoogleLogin}
                loading={loading && googleLoginState.loading}
                disabled={loading}
              >
                <GoogleIcon />
                {!(loading && googleLoginState.loading) &&
                  "Sign in with Google"}
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Login;
